import { Country } from "shared";
import { useCallback, useState } from "react";
import { useEnvironment } from "../../app/Providers/AppContext";
import { downloadDocumentUrl } from "../../utils/downloadDocumentUrl";
import { apiRoutes } from "../../routing/routes";

export type UseDownloadIdCardResult = {
  download: () => Promise<void>;
  isDownloading: boolean;
  error: Error | undefined;
};

export const useDownloadIdCard = (
  policyNumber: string,
  country: Country
): UseDownloadIdCardResult => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<Error>();
  const env = useEnvironment();

  const download = useCallback(async () => {
    setIsDownloading(true);

    const url = apiRoutes.idCardUrl(country, policyNumber);

    // Can't download locally so show an alert with the URL
    // Comment this if you want to see the error message
    if (env === "localhost") {
      alert(`Download id card: ${url}`);
      setIsDownloading(false);
      return;
    }

    const response = await fetch(url, {
      headers: {
        "content-type": "application/json",
      },
    });

    if (!response || !response.ok) {
      setError(new Error("Couldn't fetch id cards url"));
      setIsDownloading(false);
      return;
    }

    const idCardUrl = await response.text();

    const documentName = `${policyNumber}_${country}_id_card.pdf`;
    const didDownload = await downloadDocumentUrl(idCardUrl, documentName);

    setIsDownloading(false);
    setError(
      didDownload ? undefined : new Error("Unable to launch id cards url")
    );
  }, [policyNumber, country]);

  return { download, isDownloading, error };
};
